import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { DeleteDialogComponent, GlobalPageService, TableDataSetModule } from '../../';
import { ListMapDataSetModule } from '../../list-map-data-set';

import {
  AnyObject,
  CommonDataSetAction,
  CrudFunctionProp,
  CurdDataSetConfig,
  CurdDataSetConfigList, ItemAttrListDataSet,
  ListDataSetConfig
} from '@mri/types';
import { AuthResourceService, ConfigResourceService, GenericResourceService } from '@mri/app/resources';
import { filter, mergeMap, Observable, Subscription, tap } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { getQueryParams } from '@mri/functions';

@Component({
  selector: 'app-crud-data-set-list',
  standalone: true,
  imports: [
    TableDataSetModule,
    ListMapDataSetModule
  ],
  template: `
    @if (listConfig) {
      <app-list-map-data-set 
        [config]="listConfig" 
        [filterValueRecord]="filterValue"
        [autoShowFilter]="autoShowFilter" 
        [size]="size || 50"
        [autoSize]="autoSize"
      />
    }
  `
})
export class CrudDataSetListComponent implements OnInit, OnDestroy {
  private _subscription = new Subscription();
  private _params?: Record<string, string>;
  listConfig!: ListDataSetConfig;
  filterValue?: AnyObject;
  autoShowFilter: boolean = false;
  size?: number;
  autoSize?: boolean;

  constructor(
    @Inject('CRUD_DATA_FORM_CONFIG') private _config: CurdDataSetConfig,
    private _genericSrv: GenericResourceService,
    private _globalPageSrv: GlobalPageService,
    private _authSrv: AuthResourceService,
    private _configSrv: ConfigResourceService,
    private route: Router,
    private _activateRoute: ActivatedRoute
  ) {
  }

  ngOnInit(): void {

    this._params = getQueryParams();

    this._subscription.add(
      this._authSrv.authFirst$
        .pipe(
          filter(auth => !!auth),
          tap((auth) => auth && this._initComponent({
            auth, genericSrcSrv: this._genericSrv,
            params: this._params || {},
            configSrcSrv: this._configSrv,
            action: 'list'
          }))
        )
        .subscribe()
    );
  }

  ngOnDestroy() {
    this._subscription.unsubscribe();
  }

  private _initComponent(prop: CrudFunctionProp): void {
    const { list } = this._config;

    if (list === undefined) return;

    if (typeof list === 'function') {
      this._initComponentFunction(prop);
      return;
    }

    this.initTableConfig(list);
  }

  private _initComponentFunction(prop: CrudFunctionProp): void {
    const { list } = this._config;

    if (typeof list !== 'function') return;

    const listResult = list(prop);

    if (listResult instanceof Observable) {
      this._subscription.add(listResult.subscribe(config => this.initTableConfig(config)));

      return;
    }

    this.initTableConfig(listResult);
  }

  private initTableConfig(listConfig: CurdDataSetConfigList): void {
    const queryParams = this._params || {};
    let filterValue: Record<string, string> | undefined;

    Object.keys(queryParams)
      .forEach(key => {
        if (!key.startsWith('$filter:')) return;

        const value = this._params?.[key];
        const keyParam = key.replace('$filter:', '');
        filterValue = { ...(filterValue || {}), [keyParam]: value } as Record<string, string>;
      });

    this.filterValue = filterValue;

    const { gatewayUrl, baseBreadcrumb, collectionName } = this._config;

    const {
      pageConfig,
      table,
      extraActions,
      actionsKeys, filter: filterConfig,
      searchFilter,
      card,
      defaultMode,
      onMapReady,
      onLoadElements,
      onSelectedElements,
      onDlbClickElements,
      saverFilterExclude,
      extraQueryFilter,
      initConfig
    } = listConfig;

    if(Number.isFinite(initConfig?.size)){
      this.size = initConfig?.size;
    }

    this.autoSize = initConfig?.autoSize;

    this._globalPageSrv.setTitle(pageConfig?.title || `List ${collectionName}`);

    this._globalPageSrv.setBreadcrumb(pageConfig?.breadcrumb || [
      {
        url: `${baseBreadcrumb}/list`,
        title: collectionName,
        link: `${baseBreadcrumb}/list`,
        type: 'item'
      },
      {
        pageName: `List ${collectionName}`,
        url: `${baseBreadcrumb}/list`,
        title: 'List',
        link: `${baseBreadcrumb}/list`,
        type: 'item'
      }
    ]);

    const actions: Array<CommonDataSetAction> = [];

    /**actions.push({
      label: 'Fijar',
      icon: 'fi fi-rr-thumbtack',
      onlyIcon: () => true,
      key: 'fix',
      disableWhen: ({ selectedElements }) => selectedElements?.length !== 1,
      click: prop => {
        const { element, selectedElements, component } = prop;
        const [elementSelected] = element ? [element] : (selectedElements || []);

        component?.toggleFix(elementSelected);
      }
    });**/

    if (actionsKeys?.includes('create')) {
      actions.push({
        label: 'Crear',
        link: ['..', 'create'],
        icon: 'ti-plus',
        key: 'create'
      });
    }

    if (actionsKeys?.includes('detail')) {
      actions.push({
        label: 'Detalles',
        icon: 'fi fi-rr-eye',
        link: ({ selectedElements }) => selectedElements?.[0]?._id ? ['..', selectedElements?.[0]?._id, 'view'] : null,
        inRow: true,
        key: 'view',
        disableWhen: ({ selectedElements }) => selectedElements?.length !== 1
      });
    }

    if (actionsKeys?.includes('edit')) {
      actions.push({
        label: 'Editar',
        icon: 'fi fi-rr-pencil',
        link: ({ selectedElements }) => selectedElements?.[0]?._id ? ['..', selectedElements?.[0]?._id, 'edit'] : null,
        inRow: true,
        key: 'edit',
        disableWhen: ({ selectedElements }) => selectedElements?.length !== 1
      });
    }

    if (actionsKeys?.includes('delete')) {
      actions.push({
        label: 'Eliminar',
        icon: 'fi fi-rr-trash',
        inRow: true,
        key: 'delete',
        disableWhen: ({ selectedElements }) => selectedElements?.length === 0,
        click: prod => {
          const { selectedElements, component } = prod;
          const firstColumn = table?.columns?.[0];
          const titleStructure = table?.structure?.title || card?.structure?.title;
          const getValueFn = titleStructure || firstColumn?.getValue;

          const elementNames = selectedElements?.map(element => {
            if (typeof getValueFn !== 'function') return element?.[firstColumn?.key || ''] || `Objecto (${element?._id})`;

            const prod: ItemAttrListDataSet = {
              elements: [element],
              filterValueRecord: {},
              marks: {},
              selectedElements: [element],
              totalCount: 1,
              element
            };
            return getValueFn(prod) || `Objecto (${element._id})`;
          });

          const dialogSrv = component?.dialogSrv;
          const genericResourceSrv = component?.genericResourceSrv;

          if (!dialogSrv || !genericResourceSrv) return;

          const dialog = dialogSrv?.open(DeleteDialogComponent, {
            data: { elementNames },
            autoFocus: false, disableClose: true
          });

          return dialog.afterClosed()
            .pipe(
              filter(result => !!result),
              mergeMap(() => this._genericSrv.deleteElements(gatewayUrl, {
                filter: { id: selectedElements?.map(e => e._id) }
              })),
              tap(() => component?.reload())
            );
        }
      });
    }

    actions.push(...((extraActions || []) as AnyObject[]));

    if (actionsKeys?.includes('export-xls')) {
      actions.push({
        label: 'Exportar en hoja de calculo',
        icon: 'fi fi-rr-file-spreadsheet',
        onlyIcon: () => true,
        key: 'export-xls',
        click: prod => {
          return prod.component?.exportExcel()
        }
      });
    }

    /*
    if (actionsKeys?.includes('export-pdf')) {
      actions.push({
        label: 'Exportar en hoja de PDF',
        icon: 'fi fi-rr-file-pdf',
        onlyIcon: () => true,
        key: 'export-pdf',
        click: prod => {
          return prod.component?.exportPdf()
        }
      });
    }*/

    const listConfigNew: ListDataSetConfig = {
      onMapReady,
      onLoadElements,
      onSelectedElements,
      onDlbClickElements,
      hasSocket: card?.hasSocket || table?.hasSocket,
      modeViewDefault: (queryParams?.['$view:mode'] as 'table') || defaultMode,
      structure: card?.structure || table?.structure,
      ...(table || {}),
      actions,
      gatewayUrl,
      filter: filterConfig,
      searchFilter: searchFilter,
      saveFilterExclude: saverFilterExclude,
      extraQueryFilter
    };

    if ('$view:initLoad' in queryParams)
      listConfigNew.noInitLoad = !(/true/i).test(<string>queryParams['$view:initLoad']);

    if ('$view:showFilter' in queryParams)
      this.autoShowFilter = (/true/i).test(<string>queryParams['$view:showFilter']);

    this.listConfig = listConfigNew;
  }


}
